import { EVMAnalyticsEvent } from "./events"
import type { TVMFunnelKey } from "./funnels"
import { EVMAssetOutputType, EVMAssetSurface } from "./propertyEnums"
import type { IVMFlowNode } from "./types"
import { EVMFlowStatus } from "./types"

type TVMFlowNode = IVMFlowNode<TVMFunnelKey>

/**
 * The product flow hierarchy, mirroring the Figma tree. The doc generator renders this
 * as Mermaid so a human can see which surface is measured by which funnel — and, more
 * usefully, which surface is measured by nothing.
 *
 * `funnel` is constrained to real keys of VM_ANALYTICS_FUNNELS, so a renamed funnel
 * breaks the build here rather than leaving a diagram that quietly lies.
 *
 * **`status` is a claim the audit checks, not a label.** Every leaf declares whether it
 * actually ships and actually emits; `analytics-audit` fails a SHIPPED_INSTRUMENTED leaf
 * whose surface has no fire site. Without it this file mixes shipped surfaces with design
 * intent and reads as an inventory when it is really a wish — the statuses below were set
 * from a codebase sweep, not from the Figma tree.
 */
export const VM_PRODUCT_FLOWS = {
  genericCreator: {
    label: "Generic Creator",
    description: "Single-shot creation from a prompt, no campaign scaffolding.",
    children: {
      imageGeneration: {
        label: "Image generation",
        status: EVMFlowStatus.SHIPPED_BLIND,
        codePaths: ["apps/vidmax-api/src/service/vidmax/images.ts"],
        surface: EVMAssetSurface.GENERIC_CREATOR,
        outputType: EVMAssetOutputType.IMAGE,
        funnel: "coreAssetCreation",
      },
      videoGeneration: {
        label: "Video generation",
        status: EVMFlowStatus.SHIPPED_INSTRUMENTED,
        codePaths: ["apps/vidmax-api/src/service/video/orchestration.ts", "apps/vidmax-api/src/router/video.ts"],
        surface: EVMAssetSurface.GENERIC_CREATOR,
        outputType: EVMAssetOutputType.VIDEO,
        funnel: "assetToDownload",
      },
      audioGeneration: {
        label: "Audio generation",
        status: EVMFlowStatus.SHIPPED_BLIND,
        codePaths: ["apps/vidmax-api/src/service/audio.ts"],
        surface: EVMAssetSurface.GENERIC_CREATOR,
        outputType: EVMAssetOutputType.AUDIO,
        funnel: "coreAssetCreation",
      },
    },
  },

  marketingStudio: {
    label: "Marketing Studio",
    description: "Campaign-shaped surfaces that compose several generations into an ad.",
    children: {
      adStudio: {
        label: "Ad Studio",
        status: EVMFlowStatus.PLANNED,
        surface: EVMAssetSurface.AD_STUDIO,
        outputType: EVMAssetOutputType.MIXED,
        funnel: "assetToDownload",
      },
      talkingAvatar: {
        label: "Talking Avatar",
        status: EVMFlowStatus.SHIPPED_INSTRUMENTED,
        codePaths: [
          "apps/vidmax-vite/src/components/UGCWorkspace",
          "apps/vidmax-api/src/service/userUgc.ts",
          "apps/vidmax-api/src/inngest/handlers/handleUgcGeneration.ts",
        ],
        surface: EVMAssetSurface.TALKING_AVATAR,
        outputType: EVMAssetOutputType.VIDEO,
        funnel: "assetToDownload",
      },
      broll: {
        label: "B-roll",
        status: EVMFlowStatus.SHIPPED_BLIND,
        codePaths: ["apps/vidmax-api/src/router/broll.ts", "apps/vidmax-vite/src/routes/b-roll.tsx"],
        surface: EVMAssetSurface.BROLL,
        outputType: EVMAssetOutputType.VIDEO,
        funnel: "coreAssetCreation",
      },
      directorEngine: {
        label: "Director Engine",
        status: EVMFlowStatus.PLANNED,
        description: "Orchestrated multi-stage generation. The only surface with a storyboard step.",
        surface: EVMAssetSurface.DIRECTOR_ENGINE,
        outputType: EVMAssetOutputType.VIDEO,
        funnel: "directorActivation",
        events: [EVMAnalyticsEvent.STORYBOARD_CREATED],
      },
      staticAds: {
        label: "Static Ads",
        status: EVMFlowStatus.SHIPPED_BLIND,
        codePaths: ["apps/vidmax-api/src/router/staticAds.ts", "apps/vidmax-vite/src/routes/ads/static.tsx"],
        surface: EVMAssetSurface.STATIC_ADS,
        outputType: EVMAssetOutputType.IMAGE,
        funnel: "coreAssetCreation",
      },
    },
  },

  editing: {
    label: "Editing",
    description: "Surfaces that transform or compose assets that already exist.",
    children: {
      videoEditor: {
        label: "Video Editor",
        status: EVMFlowStatus.SHIPPED_INSTRUMENTED,
        codePaths: ["apps/vidmax-api/src/service/video/video-remotion.ts"],
        surface: EVMAssetSurface.VIDEO_EDITOR,
        outputType: EVMAssetOutputType.VIDEO,
        funnel: "assetToDownload",
        events: [
          EVMAnalyticsEvent.EDITOR_OPENED,
          EVMAnalyticsEvent.EDITOR_ASSET_MODIFIED,
          EVMAnalyticsEvent.VIDEO_RENDER_REQUESTED,
        ],
      },
      carouselEditor: {
        label: "Carousel Editor",
        status: EVMFlowStatus.SHIPPED_BLIND,
        codePaths: ["apps/vidmax-api/src/router/carouselEditor.ts"],
        surface: EVMAssetSurface.CAROUSEL_EDITOR,
        outputType: EVMAssetOutputType.IMAGE,
        funnel: "coreAssetCreation",
      },
    },
  },
} satisfies Record<string, TVMFlowNode>

/**
 * How many SHIPPED_INSTRUMENTED leaves may still lack a verified `surface` emission.
 *
 * Every such leaf is instrumented with the LEGACY events but does not yet emit `surface`,
 * because the ASSET_CREATE_* fire sites are not wired. `analytics-audit` errors when the
 * real count exceeds this, so the number can only fall — a new leaf cannot claim
 * SHIPPED_INSTRUMENTED without actually emitting. Drops to 0 when the migration lands.
 */
export const VM_UNVERIFIED_INSTRUMENTED_BUDGET = 3

export type TVMFlowKey = keyof typeof VM_PRODUCT_FLOWS

/** Depth-first walk yielding every node with its path. Used by the doc generator and audit. */
export const walkProductFlows = (
  nodes: Record<string, TVMFlowNode> = VM_PRODUCT_FLOWS,
  path: string[] = [],
): { path: string[]; key: string; node: TVMFlowNode }[] =>
  Object.entries(nodes).flatMap(([key, node]) => [
    { path: [...path, key], key, node },
    ...(node.children ? walkProductFlows(node.children, [...path, key]) : []),
  ])

/** Every surface referenced by a leaf of the hierarchy. */
export const getFlowSurfaces = (): EVMAssetSurface[] => [
  ...new Set(
    walkProductFlows()
      .map(({ node }) => node.surface)
      .filter((surface): surface is EVMAssetSurface => surface !== undefined),
  ),
]
